<template>
  <HgModal v-model="internalModel" @close="onClose" @keyup.enter="onSwitchToAgency">
    <template #title>{{ t('agency.cccAccess.modalTitle') }}</template>
    <template #default>
      <div>
        <HgInput
          v-model="agencyId"
          data-cy="enter-agency-id-input"
          :label="t('agency.cccAccess.enterAgencyId')"
          updateModelOnInput
          :isInvalid="hasError"
          :isDirty="hasError || isValidAgencyId"
          name="agency-id"
        >
          <template #errorMessages
            ><span data-cy="enter-agency-id-input-error-msg">{{
              t('agency.cccAccess.agencyNotFound')
            }}</span>
          </template>
          <template #successMessages
            ><span data-cy="enter-agency-id-input-success-msg">{{
              t('agency.cccAccess.agencyFound')
            }}</span>
          </template>
        </HgInput>
      </div>
    </template>
    <template #footer>
      <HgButton
        data-cy="modal-switcher-agency-close-btn"
        :disabled="isLoading"
        theme="primary-bordered"
        @click="onClose"
        >{{ t('agency.cccAccess.close') }}</HgButton
      >
      <HgButton
        data-cy="modal-switcher-agency-ok-btn"
        :disabled="isOkButtonDisabled"
        @click="onSwitchToAgency"
        >{{ t('agency.cccAccess.switchToAgency') }}</HgButton
      >
    </template>
  </HgModal>
</template>

<script lang="ts" setup>
import { HgButton, HgInput, HgModal } from '@smg-real-estate/domus';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { EventAction } from '@/services/tracking/GtmTrackingService';
import { trackClickedElement } from '@/services/tracking/Tracking';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { useAppStore } from '@/stores/app/useAppStore';
import { ToastTheme } from '@/stores/types/app';

interface Props {
  modelValue: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue', 'onClose']);
const appStore = useAppStore();

const agencyStore = useAgencyStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const agencyId = ref<string>('');
const isLoading = ref<boolean>(false);
const isValidAgencyId = ref<boolean>(false);
const hasError = ref<boolean>(false);
const isOkButtonDisabled = computed(
  () => agencyId.value.length < 3 || isLoading.value || hasError.value,
);
const internalModel = computed({
  get() {
    return props.modelValue;
  },
  set(val: boolean) {
    emit('update:modelValue', val);
  },
});

const onClose = () => {
  if (isLoading.value) return;
  emit('onClose');
};

watch(agencyId, () => {
  hasError.value = false;
});
watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      resetData();
    }
  },
);

const resetData = () => {
  agencyId.value = '';
  isLoading.value = false;
  isValidAgencyId.value = false;
  hasError.value = false;
};

const handleAgencySwitch = async () => {
  await agencyStore.switchToAgency(agencyId.value);
  emit('onClose');
  appStore.showToast({
    title: t('agency.cccAccess.successToast.title'),
    message: `${t('agency.cccAccess.successToast.message')} ${agencyId.value}`,
    theme: ToastTheme.Success,
  });
  if (route.name === 'business-listings-manage') return;
  await router.push({ name: 'business-listings-manage' });

  trackClickedElement({
    action: EventAction.SWITCH_AGENCY,
    label: `switch_to_${agencyId.value}`,
  });
};

const onSwitchToAgency = async () => {
  isLoading.value = true;
  const agencyDetails = await agencyStore.resolveAgency(agencyId.value);
  isValidAgencyId.value = !!agencyDetails;
  if (isValidAgencyId.value) return handleAgencySwitch();
  hasError.value = true;
  isLoading.value = false;
};
</script>
